import { useEffect } from 'react'
import get from 'lodash/get'
import { useSelector, useDispatch } from 'react-redux'
import { RedirectToHomeDigest } from '../Navigation'

// Actions
import { searchYlopoListings } from '../../../actions/listings'

// Components
import { InstantOfferButton } from '../EstimatedValue/InstantOffer/InstantOfferButton'
import { Page, BotHeadSpin } from '../..'
import { OverlayQuestionsFooter } from '../Routing'
import { SlideOnMount } from '../Animations'

import {
  selectShouldRenderMarketPanel,
  selectShouldRenderEquitySmartSearchFeatures,
} from '../../../store/selectors/display'
import { selectHomeownerMarketLoading } from '../../../store/selectors/marketRecord'
import {
  selectDisplayYlopoSpecificContent,
  selectHasListingsEnabled,
} from '../../../store/selectors/listings'
import { useTrackHomeEventOnce } from '../../../hooks'
import { MARKET_PANEL_VIEW_EVENT } from '../../../constants/client/clientEvents'
import {
  SHOULD_SELL_MARKET_TOPIC_KEY,
  HOMEOWNER_MARKET_TOPIC_KEY,
} from '../../../constants/topicKeys'
import { QUESTIONS } from '../../../constants/messages'
import MarketSpotlightText from './MarketSpotlightText'
import MarketSpotlightChart from './MarketSpotlightChart'
import MarketOverview from './MarketOverview'
import { MarketFacts } from './MarketFacts'
import MarketCash from './MarketCash'
import MarketSpotlightVideo from './MarketSpotlightVideo'
import { EquitySmartSearchTable } from './EquitySmartSearchTable'
import { EquitySmartSearchWithListings } from './EquitySmartSearchWithListings'
import { MarketSimilarHomes } from './MarketSimilarHomes'
import ThinkingOfSelling from './ThinkingOfSelling'
import { useIntl } from 'react-intl'

// helpers
import { loadYlopo } from '../../../helpers/ylopo'

// Styles
import styles from './MarketSpotlight.module.scss'

const questionMessages = [QUESTIONS.shouldSellNow, QUESTIONS.marketNeighborhood]

export const MarketSpotlight = () => {
  const dispatch = useDispatch()
  const trackViewedEvent = useTrackHomeEventOnce(MARKET_PANEL_VIEW_EVENT)
  const intl = useIntl()

  const showYlopoListings = useSelector(selectDisplayYlopoSpecificContent)
  const showListings = useSelector(selectHasListingsEnabled)

  useEffect(() => {
    if (showYlopoListings) {
      loadYlopo(() => {
        window['YLOPO_WIDGETS'].widgetOnloadCallback = (
          _widgetType,
          listings,
          searchArgs
        ) => {
          // use search tags in data-search param to control multiple result sets
          dispatch(
            searchYlopoListings(listings, get(searchArgs, 'search.tag', ''))
          )
        }
      })
    }
  }, [dispatch, showYlopoListings])

  trackViewedEvent()

  const shouldRender = useSelector(selectShouldRenderMarketPanel)
  const showEquitySmartSearchFeatures = useSelector(
    selectShouldRenderEquitySmartSearchFeatures
  )

  const loading = useSelector(selectHomeownerMarketLoading)

  if (!shouldRender) {
    return <RedirectToHomeDigest />
  }

  return (
    <Page>
      <Page.Body className={styles.body} dataQa='market-spotlight'>
        {loading ? (
          <BotHeadSpin panelSpacing />
        ) : (
          <>
            <SlideOnMount>
              <MarketSpotlightText withDescription className={styles.header} />
            </SlideOnMount>
            <MarketSpotlightVideo />
            <InstantOfferButton
              colorScheme='btn-secondary'
              className={styles.instantOfferBtn}
            />
            <SlideOnMount>
              <MarketSpotlightChart className={styles.chartWrapper} />
            </SlideOnMount>
            <SlideOnMount>
              <div className={styles.marketInfo}>
                <MarketFacts />
                <MarketOverview />
              </div>
            </SlideOnMount>
            <SlideOnMount>
              <ThinkingOfSelling />
            </SlideOnMount>
            <SlideOnMount>
              {showListings && (
                <>
                  <MarketSimilarHomes />
                  {showEquitySmartSearchFeatures && (
                    <EquitySmartSearchWithListings />
                  )}
                </>
              )}
              {showEquitySmartSearchFeatures && !showListings && (
                <EquitySmartSearchTable />
              )}
              <MarketCash />
            </SlideOnMount>
          </>
        )}
        <Page.Disclaimer />
      </Page.Body>
      <Page.Footer>
        <OverlayQuestionsFooter
          topic={[
            intl.formatMessage(QUESTIONS.shouldYouSellMarketTopic),
            intl.formatMessage(QUESTIONS.marketSpotlightTopic),
            intl.formatMessage(QUESTIONS.marketSpotlightTopic), // topic for the custom "Ask a question..." DM
          ]}
          questions={Object.values(questionMessages)}
          topicKey={[
            SHOULD_SELL_MARKET_TOPIC_KEY,
            HOMEOWNER_MARKET_TOPIC_KEY,
            HOMEOWNER_MARKET_TOPIC_KEY,
          ]}
          placeholder
          disableCospo
        />
      </Page.Footer>
    </Page>
  )
}
